import axios, { type AxiosInstance, type AxiosResponse } from 'axios';

import type { PortailUserModel } from '@/models/portailUser';
import type { Token } from '@/models/users';

type Operation = 'attach' | 'detach' | 'sync' | 'create' | 'update';

type Attributes = {
   firstname?: string;
   lastname?: string;
   email?: string;
   timezone?: string;
   language?: string;
   u_code?: string;
   seller_code?: string;
   phone_number?: string;
   group_id?: string;
   profession_id?: string;
   service_id?: string;
   sector_id?: string;
   demo_request_at?: string;
};

export type RelationType = {
   operation: Operation;
   without_detaching?: boolean;
   key: string;
   pivot?: {
      profile_id?: string;
      order?: number;
   };
};

type Relations = {
   applications?: RelationType[];
   roles?: { operation: string; key: string }[];
   directManager?: { operation: string; key: string };
   site?: { operation: string; key: string };
};

export type MutateUserBody = {
   operation: 'create' | 'update';
   key?: string;
   attributes?: Attributes;
   relations?: Relations;
   number_managers_can_validate?: number;
};

class AuthService {
   private provider: AxiosInstance;

   constructor() {
      this.provider = axios.create({
         baseURL: process.env.PORTAIL_API,
         timeout: 90000,
         headers: {
            fromApp: process.env.AUTH_UUID,
            'Content-Type': 'application/json; charset=utf-8',
         },
      });
   }

   async getLoginToken(): Promise<string> {
      const response: AxiosResponse<Token> = await this.provider.post(
         '/auth/login',
         {
            email: process.env.EMAIL,
            password: process.env.PASSWORD,
            remember_me: true,
         },
      );

      return response.data.access_token;
   }

   async refreshToken(jwt: string): Promise<string> {
      const response: AxiosResponse<Token> = await this.provider.post(
         '/auth/refresh',
         {},
         {
            headers: {
               Authorization: `bearer ${jwt}`,
            },
         },
      );
      return response.data.access_token;
   }

   async logout(jwt: string): Promise<void> {
      await this.provider.post(
         '/auth/logout',
         {},
         {
            headers: {
               Authorization: `bearer ${jwt}`,
            },
         },
      );
   }

   async getUserData(id: string, jwt: string): Promise<PortailUserModel[]> {
      const response = await this.provider.post(
         `/users/search`,
         {
            search: {
               filters: [
                  {
                     field: 'id',
                     operator: '=',
                     value: id,
                  },
               ],
            },
         },
         {
            headers: {
               Authorization: `bearer ${jwt}`,
            },
         },
      );

      return response.data.data;
   }

   async mutate(
      mutate: MutateUserBody[],
      jwt: string,
   ): Promise<{ created: string; updated: string }> {
      const response = await this.provider.post(
         '/users',
         {
            mutate,
         },
         {
            headers: {
               Authorization: `bearer ${jwt}`,
            },
         },
      );

      return {
         created: response.data.created[0] ?? '',
         updated: response.data.updated[0] ?? '',
      };
   }
}

export default AuthService;
